import React, { useReducer } from 'react';
import { useAuth } from '../context/AuthContext';
import { Loader2 } from 'lucide-react';

const initialAuthState = {
    email: '',
    password: '',
    isSignUp: false,
    error: '',
    emailLoading: false,
};

function authReducer(state, action) {
    switch (action.type) {
        case 'SET_EMAIL':
            return { ...state, email: action.value };
        case 'SET_PASSWORD':
            return { ...state, password: action.value };
        case 'SET_MODE':
            return { ...state, isSignUp: action.value };
        case 'SUBMIT_START':
            return { ...state, emailLoading: true, error: '' };
        case 'SUBMIT_ERROR':
            return { ...state, error: action.error };
        case 'SUBMIT_END':
            return { ...state, emailLoading: false };
        default:
            return state;
    }
}

export default function Login() {
    const { signIn, signUp } = useAuth();
    const [state, dispatch] = useReducer(authReducer, initialAuthState);
    const { email, password, isSignUp, error, emailLoading } = state;

    const handleEmailAuth = async (e) => {
        e.preventDefault();
        dispatch({ type: 'SUBMIT_START' });

        try {
            if (isSignUp) {
                await signUp(email, password);
            } else {
                await signIn(email, password);
            }
        } catch (err) {
            dispatch({ type: 'SUBMIT_ERROR', error: err.message || 'Authentication failed' });
        } finally {
            dispatch({ type: 'SUBMIT_END' });
        }
    };

    return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-6">
            <div className="w-full max-w-[360px] bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
                <div className="text-center mb-8">
                    <h1 className="text-2xl font-bold text-blue-600 mb-1">Mekko Job Notifier</h1>
                    <p className="text-xs text-gray-500">Real-time job alerts, filtered your way</p>
                </div>

                <div className="flex border-b border-gray-200 mb-6">
                    <button
                        type="button"
                        className={`flex-1 pb-2 text-sm font-medium transition-colors ${!isSignUp ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-400 hover:text-gray-600'}`}
                        onClick={() => dispatch({ type: 'SET_MODE', value: false })}
                    >
                        Log In
                    </button>
                    <button
                        type="button"
                        className={`flex-1 pb-2 text-sm font-medium transition-colors ${isSignUp ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-400 hover:text-gray-600'}`}
                        onClick={() => dispatch({ type: 'SET_MODE', value: true })}
                    >
                        Sign Up
                    </button>
                </div>

                {error && (
                    <div className="mb-4 p-3 rounded bg-red-50 text-red-600 text-xs text-center border border-red-100">
                        {error}
                    </div>
                )}

                <form onSubmit={handleEmailAuth} className="space-y-4">
                    <div className="space-y-1">
                        <label htmlFor="email" className="text-xs font-semibold text-gray-700">Email Address</label>
                        <input
                            id="email"
                            type="email"
                            required
                            placeholder="you@example.com"
                            className="w-full h-10 px-3 rounded-lg border border-blue-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none text-sm transition-all"
                            value={email}
                            onChange={(e) => dispatch({ type: 'SET_EMAIL', value: e.target.value })}
                        />
                    </div>

                    <div className="space-y-1">
                        <label htmlFor="password" className="text-xs font-semibold text-gray-700">Password</label>
                        <input
                            id="password"
                            type="password"
                            required
                            placeholder="••••••••"
                            className="w-full h-10 px-3 rounded-lg border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none text-sm transition-all"
                            value={password}
                            onChange={(e) => dispatch({ type: 'SET_PASSWORD', value: e.target.value })}
                        />
                    </div>

                    <button
                        type="submit"
                        disabled={emailLoading}
                        className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg shadow-sm transition-all active:scale-95 flex items-center justify-center text-sm disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                        {emailLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : (isSignUp ? 'Sign Up' : 'Log In')}
                    </button>
                </form>

                <div className="relative my-5">
                    <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-100"></div></div>
                    <div className="relative flex justify-center text-xs"><span className="px-2 bg-white text-gray-400">or</span></div>
                </div>

                <a
                    href={`${import.meta.env.VITE_API_URL || ''}/api/auth/google`}
                    className="w-full py-2.5 border border-gray-200 hover:bg-gray-50 text-gray-700 font-medium rounded-lg transition-all flex items-center justify-center gap-2 text-sm"
                >
                    <svg className="w-4 h-4" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" /><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" /><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.84z" fill="#FBBC05" /><path d="M12 4.66c1.61 0 3.1.56 4.28 1.69l3.19-3.19C17.45 1.16 14.97 0 12 0 7.7 0 3.99 2.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" /></svg>
                    Continue with Google
                </a>
            </div>
        </div>
    );
}
